/**
 * /facturas — Lista de facturas/NC/ND emitidas (incluye drafts).
 *
 * Filtros: doc_type, status, fecha. Default = solo invoices, todos los status,
 * último mes.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Plus } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  invoiceApi,
  type InvoiceDocType,
  type InvoiceStatus,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { formatMoney } from '@/lib/money'

const STATUS_BADGES: Record<InvoiceStatus, { bg: string; fg: string; label: string }> = {
  draft: { bg: 'bg-bg-elevated', fg: 'text-fg-secondary', label: 'Borrador' },
  submitting: { bg: 'bg-info/15', fg: 'text-info', label: 'Enviando a DIAN' },
  accepted: { bg: 'bg-success-tint', fg: 'text-success-fg', label: 'Aceptada DIAN' },
  rejected: { bg: 'bg-error-tint', fg: 'text-error-fg', label: 'Rechazada DIAN' },
  failed: { bg: 'bg-warning-tint', fg: 'text-warning-fg', label: 'Falló' },
}

const DOC_TYPE_LABELS: Record<InvoiceDocType, string> = {
  invoice: 'Factura',
  credit_note: 'NC',
  debit_note: 'ND',
}

export function InvoicesListPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const [docType, setDocType] = useState<InvoiceDocType | ''>('')
  const [status, setStatus] = useState<InvoiceStatus | ''>('')

  const list = useQuery({
    queryKey: ['invoices', activeOrgId, docType, status],
    queryFn: () =>
      invoiceApi.list(activeOrgId!, {
        doc_type: docType || undefined,
        status: status || undefined,
        limit: 100,
      }),
    enabled: !!activeOrgId,
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Facturas</h1>
            <p className="mt-1 text-sm text-fg-secondary">
              Documentos emitidos (facturas, notas crédito, notas débito).
            </p>
          </div>
          <Link
            to={ROUTES.INVOICE_NEW}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Nueva factura
          </Link>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value as InvoiceDocType | '')}
            className="rounded-md border border-border bg-bg-app px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todos los tipos</option>
            <option value="invoice">Factura</option>
            <option value="credit_note">Nota crédito</option>
            <option value="debit_note">Nota débito</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
            className="rounded-md border border-border bg-bg-app px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="submitting">Enviando a DIAN</option>
            <option value="accepted">Aceptada DIAN</option>
            <option value="rejected">Rechazada DIAN</option>
            <option value="failed">Falló</option>
          </select>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-bg-surface">
          {list.isLoading && (
            <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          )}
          {list.isError && (
            <div className="p-6 text-sm text-error-fg">
              Error: {(list.error as Error).message}
            </div>
          )}
          {list.data && list.data.items.length === 0 && (
            <div className="p-6 text-center text-sm text-fg-muted">
              Sin documentos. Crea tu primera factura.
            </div>
          )}
          {list.data && list.data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-bg-elevated text-left text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="px-4 py-2.5">Tipo</th>
                  <th className="px-4 py-2.5">Número</th>
                  <th className="px-4 py-2.5">Fecha</th>
                  <th className="px-4 py-2.5">Cliente</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                  <th className="px-4 py-2.5">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.items.map((inv) => {
                  const b = STATUS_BADGES[inv.status]
                  return (
                    <tr key={inv.id} className="hover:bg-bg-elevated">
                      <td className="px-4 py-2.5 text-xs">{DOC_TYPE_LABELS[inv.doc_type]}</td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={ROUTES.INVOICE_DETAIL.replace(':id', inv.id)}
                          className="font-mono text-xs text-brand-600 hover:underline"
                        >
                          {inv.doc_number}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-fg-secondary">
                        {inv.issue_date.slice(0, 10)}
                      </td>
                      <td className="px-4 py-2.5 text-fg-secondary">
                        {inv.customer_legal_name}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">
                        {/* Moneda propia del documento (AT v1.9 FAR) — no COP fijo. */}
                        {formatMoney(inv.grand_total, inv.currency)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`rounded-full ${b.bg} px-2 py-0.5 text-xs ${b.fg}`}>
                          {b.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {list.data && (
          <p className="mt-3 text-xs text-fg-muted">
            Mostrando {list.data.items.length} de {list.data.total} documentos.
          </p>
        )}
      </div>
    </AppShell>
  )
}
